import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { TokenStorageService } from '../token-storage.service';
import { canSeePage, pageKeyForPath } from '../pages';
import { MigrationModeService } from '../../services/migration-mode.service';
import { firstPermittedUrl, getNavItems } from '../../containers/default-layout/_nav';
import { global_user } from '../../global/global';

/**
 * Keeps a member off pages their page access hides: `/dashboard` and every
 * `/pages/*` route. A hidden page, typed in or opened from a link or a push
 * notification, lands on the first page the member can see, in sidebar
 * order. Routes without a page key (profile, team) pass; their own guard
 * decides.
 *
 * Hiding a page is a UI rule, not a security boundary.
 */
@Injectable({ providedIn: 'root' })
export class PageAccessGuard implements CanActivate, CanActivateChild {
  constructor(
    private tokenStorage: TokenStorageService,
    private translate: TranslateService,
    private migrationMode: MigrationModeService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot): true | UrlTree {
    const access = this.tokenStorage.getPageAccess();
    const canSee = (key: string | null) => canSeePage(access, key);
    if (canSee(pageKeyForPath(route.routeConfig?.path ?? ''))) {
      return true;
    }
    const nav = getNavItems(
      this.translate,
      this.migrationMode.getCurrentMode(),
      global_user.user?.company_type || null
    );
    return this.router.parseUrl(firstPermittedUrl(nav, canSee));
  }

  canActivateChild(route: ActivatedRouteSnapshot): true | UrlTree {
    return this.canActivate(route);
  }
}
